import React, { useEffect, useRef, useState } from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateRangeFields, FormDialog, InlineMessage } from '../ui';
import { useToast } from '../../contexts/ToastContext';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';

/** Why a sheet is held, as `pricingSubmissions.windowHold` serves it. */
export interface PricingWindowHold {
  status: 'open' | 'corrected';
  owner: 'store' | 'admin';
  message: string;
  approvableAsIs: boolean;
  correctable: boolean;
  rowWindows: string[];
}

/**
 * What a store can do with its own held sheet. `canAct` is the store's flag; a sheet
 * Motzu already corrected can be approved without it, or nothing could finish it.
 */
export const storeHeldSheetActions = (
  submission: { status?: string; orgApprovalStatus?: string; windowHold?: PricingWindowHold },
  canAct: boolean
): { correct: boolean; approveAsIs: boolean; approveCorrected: boolean } => {
  const hold = submission.windowHold;
  const none = { correct: false, approveAsIs: false, approveCorrected: false };
  if (!hold || hold.owner !== 'store' || submission.status === 'rejected' || submission.orgApprovalStatus === 'approved') {
    return none;
  }
  if (hold.status === 'corrected') return { ...none, approveCorrected: true };
  return { correct: canAct && hold.correctable, approveAsIs: canAct && hold.approvableAsIs, approveCorrected: false };
};

interface CorrectRebateDatesDialogProps {
  submission: { id: string; fileName: string; windowHold?: PricingWindowHold } | null;
  /**
   * The approval that follows the correction: an admin's, or the store's own. Null
   * when someone else decides, as the store does on a vendor's private sheet.
   */
  approveAction: 'approve' | 'org-approve' | null;
  /** Queries to refresh once the sheet has moved on. */
  invalidateKeys: string[];
  onClose: () => void;
}

const post = async (path: string, body: unknown): Promise<void> => {
  const res = await makeApiCall(path, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error?.message || data?.message || 'Request failed');
  }
};

/**
 * A sheet held for its rebate dates (MOT-864): the person it belongs to sets the
 * window and approves. Prices nothing until both have gone through.
 */
export const CorrectRebateDatesDialog: React.FC<CorrectRebateDatesDialogProps> = ({
  submission,
  approveAction,
  invalidateKeys,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  // The correction went through and the approval did not: a retry only approves,
  // because a second correction is refused once the hold is closed.
  const [correctedId, setCorrectedId] = useState<string | null>(null);
  const correctedRef = useRef<string | null>(null);

  useEffect(() => {
    setStart('');
    setEnd('');
    setReason('');
    setCorrectedId(null);
    correctedRef.current = null;
  }, [submission?.id]);

  const save = useMutation({
    mutationFn: async () => {
      if (!submission) return;
      if (correctedRef.current !== submission.id) {
        await post(`/pricing-ingestion/submissions/${submission.id}/correct-window`, {
          startDate: start,
          endDate: end,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        });
        correctedRef.current = submission.id;
        setCorrectedId(submission.id);
      }
      if (approveAction) await post(`/pricing-ingestion/submissions/${submission.id}/${approveAction}`, {});
    },
    onSuccess: () => {
      invalidateKeys.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      showToast(approveAction ? 'Rebate dates corrected and sheet approved' : 'Rebate dates corrected. The store can accept the sheet now.', 'success');
      onClose();
    },
    onSettled: () => {
      // The correction may have gone through even when the approval did not.
      invalidateKeys.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
    },
  });

  const hold = submission?.windowHold;
  const alreadyCorrected = !!submission && correctedId === submission.id;
  return (
    <FormDialog
      open={!!submission}
      title="Correct the rebate dates"
      onClose={onClose}
      onSubmit={() => save.mutate()}
      submitLabel={alreadyCorrected ? 'Approve' : approveAction ? 'Save and approve' : 'Save dates'}
      submitDisabled={!alreadyCorrected && (!start || !end || end < start)}
      loading={save.isPending}
      maxWidth="sm"
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">{submission?.fileName}</Typography>
        {hold && <InlineMessage severity="warning">{hold.message}</InlineMessage>}
        {hold && hold.rowWindows.length > 0 && (
          <Typography variant="body2">The rebate rows say: {hold.rowWindows.join(', ')}</Typography>
        )}
        <Typography variant="body2">
          Enter the dates the vendor states for this rebate. They apply to the rebate rows with the problem, and nothing prices until the sheet is approved.
        </Typography>
        <DateRangeFields
          start={start}
          end={end}
          onStartChange={setStart}
          onEndChange={setEnd}
          startLabel="Rebate starts"
          endLabel="Rebate ends"
        />
        <TextField
          label="Note (optional)"
          size="small"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          inputProps={{ maxLength: 500 }}
          helperText="Where the dates came from."
        />
        {save.error && (
          <InlineMessage severity="error">
            {alreadyCorrected ? `The dates are saved, but the approval did not go through: ${(save.error as Error).message}` : (save.error as Error).message}
          </InlineMessage>
        )}
      </Stack>
    </FormDialog>
  );
};
